import { useT } from "@/3-features/i18n/useT";
import { PATHS, PRODUCER_ROLES } from "@/3-features/navigation/routes";
import { solutionPath, VALUE_PROPS } from "@/3-features/solutions/valueProps";
import { DecLogo } from "@/4-components/primitives/DecLogo";
import { COMPANY } from "@/5-shared/constants/content/company";
import { Link } from "@/5-shared/routing";

type FooterLink = { label: string; to: string };
type FooterCol = { h: string; links: FooterLink[] };

export function SiteFooter() {
  const { t } = useT();

  const COLS: FooterCol[] = [
    {
      h: t("nav.footer.colCompanies"),
      links: [
        { label: t("nav.footer.overview"), to: PATHS.companies },
        { label: t("nav.footer.howItWorks"), to: PATHS.companiesProduct },
        ...VALUE_PROPS.map((vp) => ({
          label: t(`nav.solutionLabels.${vp.slug}`),
          to: solutionPath(vp),
        })),
      ],
    },
    {
      h: t("nav.footer.colProducers"),
      links: [
        { label: t("nav.footer.overview"), to: PATHS.producers },
        { label: t("nav.footer.howItWorks"), to: PATHS.producersProduct },
        ...PRODUCER_ROLES.map((r) => ({
          label: t(`nav.roleLabels.${r.slug}`),
          to: r.path,
        })),
      ],
    },
    {
      h: t("nav.footer.colResources"),
      links: [
        { label: t("nav.decExplorer"), to: PATHS.explorer },
        { label: t("nav.faq"), to: PATHS.faq },
        { label: t("nav.footer.aboutUs"), to: PATHS.about },
        { label: t("nav.footer.contacts"), to: PATHS.contact },
      ],
    },
  ];

  return (
    <footer className="bg-zinc-950 text-zinc-400">
      <div className="shell py-16 grid gap-10 md:grid-cols-[1.4fr_1fr_1fr_1fr]">
        <div>
          <DecLogo className="mb-4 h-12 w-auto fill-white" />
          <p className="text-sm max-w-xs">{t("nav.footer.tagline")}</p>
          <p className="mt-3 text-sm max-w-xs">{t("nav.footer.disclaimer")}</p>
        </div>
        {COLS.map((c) => {
          // each column is its own navigation landmark, named by its own heading
          const headingId = `footer-col-${c.h.toLowerCase()}`;
          return (
            <nav key={c.h} aria-labelledby={headingId}>
              <p
                id={headingId}
                className="label font-semibold text-zinc-400 mb-3"
              >
                {c.h}
              </p>
              <ul className="space-y-2">
                {c.links.map(({ label, to }) => (
                  <li key={to}>
                    <Link
                      to={to}
                      className="text-sm hover:text-white transition-colors"
                    >
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          );
        })}
      </div>
      <div className="border-t border-zinc-800">
        <div className="shell py-5 flex flex-col sm:flex-row items-center justify-between gap-2 footnote">
          <span>&copy; {COMPANY.legalName}</span>
          <div className="flex items-center gap-4">
            <a
              href={PATHS.privacyPolicy}
              className="hover:text-white transition-colors"
            >
              {t("nav.footer.privacy")}
            </a>
            <a
              href={`mailto:${COMPANY.email}`}
              className="hover:text-white transition-colors"
            >
              {COMPANY.email}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
